import Link from 'next/link';
import { motion } from 'framer-motion';
import { DemoCtaLink } from '../DemoCtaLink';

/**
 * The content sections for /quote-builder, in page order. Split out of the
 * page file so the page reads as an outline.
 *
 * Marketing copy describes the quote-request workflow. Subscription terms,
 * account limits, and signup behavior stay in their existing application code.
 */

const RISE = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 },
};

function Eyebrow({ children }) {
  return (
    <span className='mb-5 inline-block rounded-full border border-jade-deep/40 bg-jade-deep/15 px-3 py-1 font-sans text-[11px] uppercase tracking-[0.25em] text-jade-soft'>
      {children}
    </span>
  );
}

function Heading({ children }) {
  return (
    <h2 className='font-display text-3xl font-extrabold leading-[1.1] text-white [text-wrap:balance] sm:text-4xl md:text-5xl'>
      {children}
    </h2>
  );
}

const AUDIENCE = [
  'Custom manufacturers',
  'Cabinet, door, and millwork shops',
  'Fabricators and machine shops',
  'Furniture and fixture makers',
  'Sign, display, and architectural-product shops',
  'Anyone selling configured or made-to-order products',
];

export function BuiltForCustomWork() {
  return (
    <section className='px-6 py-16 md:py-24'>
      <div className='container mx-auto max-w-5xl'>
        <motion.div {...RISE}>
          <Eyebrow>Built for custom work</Eyebrow>
          <Heading>If the price depends on the details, this is for you.</Heading>

          <div className='mt-8 grid gap-10 md:grid-cols-2'>
            <div className='space-y-4 text-lg leading-relaxed text-muted'>
              <p>
                Standard contact forms weren’t made for custom products. They
                give customers an empty box and leave you to figure out what
                they actually want.
              </p>
              <p>
                The Quote Builder replaces that empty box with a guided product
                form built around the information you need to quote.
              </p>
            </div>

            <div className='rounded-2xl border border-line bg-surface/60 p-6 md:p-8'>
              <p className='mb-4 text-[11px] uppercase tracking-[0.2em] text-muted'>
                Built for
              </p>
              <ul className='space-y-3'>
                {AUDIENCE.map((item) => (
                  <li key={item} className='flex gap-3 text-white'>
                    <span
                      className='mt-2 h-1.5 w-1.5 flex-none rounded-full bg-jade'
                      aria-hidden='true'
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className='mt-8 max-w-3xl text-lg leading-relaxed text-muted'>
            If your answer to “How much does it cost?” is usually “It depends,”
            the Quote Builder helps you collect what it depends on.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

const SPEC_ROWS = [
  ['Product', 'Shaker door'],
  ['Width × height', '23.5″ × 41″'],
  ['Material', 'Walnut'],
  ['Panel', 'Flat panel with glass insert'],
  ['Rails and stiles', '2.5″'],
  ['Finish', 'Matte'],
  ['Quantity', '14'],
];

export function TheSameCustomerTwice() {
  return (
    <section className='border-y border-line/60 bg-onyx/40 px-6 py-16 md:py-24'>
      <div className='container mx-auto max-w-5xl'>
        <motion.div {...RISE}>
          <Eyebrow>From vague inquiry to quote-ready request</Eyebrow>
          <Heading>The same customer, twice.</Heading>

          <div className='mt-10 grid gap-6 md:grid-cols-2'>
            {/* Before */}
            <div className='rounded-2xl border border-line bg-obsidian/60 p-6 md:p-8'>
              <p className='mb-4 text-[11px] uppercase tracking-[0.2em] text-muted'>
                A typical inquiry
              </p>
              <blockquote className='text-lg leading-relaxed text-white'>
                “Hi—do you make shaker doors? I need some for a kitchen, maybe
                30 inches. What would that cost? Thanks.”
              </blockquote>
              <p className='mt-5 leading-relaxed text-muted'>
                Now you have to ask about the exact size, material, panel,
                finish, quantity, and everything else that affects the price.
              </p>
            </div>

            {/* After */}
            <div className='rounded-2xl border border-jade-deep/50 bg-obsidian/60 p-6 md:p-8'>
              <p className='mb-4 text-[11px] uppercase tracking-[0.2em] text-jade-soft'>
                With the Quote Builder
              </p>
              <dl className='space-y-2.5'>
                {SPEC_ROWS.map(([term, value]) => (
                  <div
                    key={term}
                    className='flex flex-wrap justify-between gap-x-4 gap-y-1 border-b border-line/60 pb-2.5 last:border-0'
                  >
                    <dt className='text-muted'>{term}</dt>
                    <dd className='font-medium text-white'>{value}</dd>
                  </div>
                ))}
              </dl>
              <p className='mt-5 leading-relaxed text-muted'>
                Everything you need to start pricing, collected in one
                structured request.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Screenshot frames.
 *
 * Two deliberately different treatments, because the two captures show
 * opposite sides of the product and the page should not blur them:
 *
 *   AppShot     — dark admin UI. Matte gradient frame, matching the
 *                 treatment SeeInActionSection uses, so it reads as
 *                 TimberCloud's own interface.
 *   BrowserShot — the shop's public storefront, captured in light mode.
 *                 Wrapped in browser chrome so the light-on-dark jump
 *                 reads as "this is your website", not a styling slip.
 *
 * Both carry intrinsic width/height so the reserved box is correct before
 * the image decodes. Sources are 1x captures — re-shoot at 2x if these
 * ever move above the fold.
 */

function ShotCaption({ children }) {
  return (
    <p className='mt-3 text-center text-sm leading-relaxed text-muted lg:text-left'>
      {children}
    </p>
  );
}

function AppShot({ src, alt, width, height, caption }) {
  return (
    <div>
      <div className='rounded-[18px] bg-gradient-to-br from-white/[0.07] via-white/[0.025] to-white/[0.04] p-1.5 ring-1 ring-white/[0.08] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] md:p-2'>
        <div className='overflow-hidden rounded-xl bg-obsidian'>
          <img
            src={src}
            alt={alt}
            width={width}
            height={height}
            className='block h-auto w-full'
            loading='lazy'
            decoding='async'
          />
        </div>
      </div>
      {caption ? <ShotCaption>{caption}</ShotCaption> : null}
    </div>
  );
}

function BrowserShot({ src, alt, width, height, caption }) {
  return (
    <div>
      <div className='overflow-hidden rounded-xl border border-line bg-surface shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]'>
        {/* Chrome. Decorative only — the address is a stand-in for the
            customer's own domain, not a real TimberCloud URL. */}
        <div
          aria-hidden='true'
          className='flex items-center gap-2 border-b border-line bg-onyx px-3 py-2.5'
        >
          <span className='flex gap-1.5'>
            <span className='h-2.5 w-2.5 rounded-full bg-[#ffffff]/20' />
            <span className='h-2.5 w-2.5 rounded-full bg-[#ffffff]/20' />
            <span className='h-2.5 w-2.5 rounded-full bg-[#ffffff]/20' />
          </span>
          <span className='ml-1 flex-1 truncate rounded-full bg-obsidian/80 px-3 py-1 text-[11px] text-muted'>
            yourwebsite.com
          </span>
        </div>
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          className='block h-auto w-full'
          loading='lazy'
          decoding='async'
        />
      </div>
      {caption ? <ShotCaption>{caption}</ShotCaption> : null}
    </div>
  );
}

/**
 * InlineCta — the mid-page signup prompts.
 *
 * Deliberately lighter than QuoteBuilderCta: a strip, not a section. The page
 * should still end on one clear conversion moment, and three full-width
 * centred CTAs would make it feel like it keeps ending.
 *
 * Each placement carries its own line rather than repeating one string, so a
 * reader scrolling past both doesn't see the same sentence twice. The button
 * label is the constant — it is the same offer and the same destination.
 */
function InlineCta({ headline, note }) {
  return (
    <section className='px-6 py-6 md:py-8'>
      <motion.div
        {...RISE}
        className='container mx-auto flex max-w-5xl flex-col gap-5 rounded-2xl border border-jade-deep/40 bg-jade-deep/[0.07] px-6 py-6 sm:flex-row sm:items-center sm:justify-between md:px-8'
      >
        <div>
          <p className='text-lg font-bold text-white'>{headline}</p>
          <p className='mt-1 text-sm text-muted'>{note}</p>
        </div>
        <Link
          href='/demo'
          className='inline-flex flex-none items-center justify-center gap-2 rounded-full bg-jade-cta px-7 py-3.5 font-semibold text-obsidian transition-colors hover:bg-jade-cta-deep'
        >
          Book a demo
          <span aria-hidden='true'>→</span>
        </Link>
      </motion.div>
    </section>
  );
}

export function InlineCtaAfterContrast() {
  return (
    <InlineCta
      headline='Give customers a better way to ask.'
      note='Walk through a representative product with our team.'
    />
  );
}

export function InlineCtaAfterHowItWorks() {
  return (
    <InlineCta
      headline='Start with a product you quote often.'
      note='Walk through a representative product with our team.'
    />
  );
}

const STEPS = [
  {
    title: 'Set up your products',
    body: 'Create the products you quote most often. Define the dimensions, materials, finishes, quantities, and options that change the price.',
    shot: {
      Frame: AppShot,
      src: '/screenshots/quote-builder-attributes.png',
      alt: 'Product setup in TimberCloud showing attributes for sheet material, back panel, shelves, wood species and finish, above line item fields for quantity, width, height and depth.',
      width: 829,
      height: 569,
      caption:
        'Attributes and line item fields are defined once per product, then reused on every request.',
    },
  },
  {
    title: 'Add it to your website',
    body: 'Embed the Quote Builder on the site you already have — whether you use WordPress, Squarespace, Wix, Shopify, or something custom.',
  },
  {
    title: 'Receive quote-ready requests',
    body: 'Customers configure what they need and submit their contact information. You receive a complete, structured request.',
    reverse: true,
    shot: {
      Frame: BrowserShot,
      src: '/screenshots/quote-builder-request-quote.png',
      alt: 'Two product cards on a shop website — Cabinet Box and Bookshelf — each with a Request a Quote link.',
      width: 690,
      height: 359,
      caption:
        'What your customers see: products they can configure, with a request instead of a checkout.',
    },
  },
];

export function HowItWorks() {
  return (
    <section id='how-it-works' className='px-6 py-16 md:py-24'>
      <div className='container mx-auto max-w-5xl'>
        <motion.div {...RISE}>
          <Eyebrow>How it works</Eyebrow>
          <Heading>Build it once. Add it to your site. Get better requests.</Heading>

          {/* Numbered because the order genuinely carries meaning here.
              Steps 1 and 3 carry a screenshot and alternate sides; step 2
              is text only, which also keeps the two shots from colliding
              on the same edge of the page. */}
          <ol className='mt-10 space-y-12 md:space-y-16'>
            {STEPS.map((step, i) => {
              const shot = step.shot;
              const Frame = shot?.Frame;

              return (
                <li
                  key={step.title}
                  className='grid grid-cols-[2.5rem_1fr] gap-x-4 gap-y-6'
                >
                  <span className='flex h-10 w-10 items-center justify-center rounded-full border border-jade-deep/60 bg-jade-deep/20 font-semibold text-jade-soft'>
                    {i + 1}
                  </span>

                  <div
                    // items-start, not items-center: the step number lives in
                    // the outer rail and would visibly detach from its own
                    // heading if the copy were centred against the taller
                    // screenshot.
                    //
                    // The column ratio flips with the row. Grid assigns tracks
                    // by order, not by source position, so a reversed row that
                    // only sets order-first would hand the screenshot the
                    // narrow 5fr track and shrink it against the other shot.
                    className={[
                      shot ? 'grid items-start gap-6 lg:gap-10' : 'max-w-2xl',
                      shot && step.reverse ? 'lg:grid-cols-[6fr_5fr]' : '',
                      shot && !step.reverse ? 'lg:grid-cols-[5fr_6fr]' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <div className={step.reverse ? 'lg:order-last' : undefined}>
                      <h3 className='text-xl font-bold text-white'>
                        {step.title}
                      </h3>
                      <p className='mt-2 leading-relaxed text-muted'>
                        {step.body}
                      </p>
                    </div>

                    {shot ? (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.97 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className={step.reverse ? 'lg:order-first' : undefined}
                      >
                        <Frame
                          src={shot.src}
                          alt={shot.alt}
                          width={shot.width}
                          height={shot.height}
                          caption={shot.caption}
                        />
                      </motion.div>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ol>
        </motion.div>
      </div>
    </section>
  );
}

const BENEFITS = [
  'Complete specifications instead of vague inquiries',
  'Fewer follow-up questions',
  'Less time spent chasing dimensions and options',
  'Faster, more confident quoting',
  'A clearer experience for your customers',
  'Every request and customer kept in one place',
  'Reusable product configurations you only build once',
];

export function WhyItsWorthIt() {
  return (
    <section className='border-y border-line/60 bg-onyx/40 px-6 py-16 md:py-24'>
      <div className='container mx-auto max-w-5xl'>
        <motion.div {...RISE}>
          <Eyebrow>Why it’s worth it</Eyebrow>
          <Heading>Stop doing discovery over email.</Heading>

          <div className='mt-8 grid gap-10 md:grid-cols-2'>
            <div className='space-y-4 text-lg leading-relaxed text-muted'>
              <p>
                An incomplete inquiry creates another email, another phone call,
                and another chance for the customer to move on.
              </p>
              <p>
                The Quote Builder collects the important details while the
                customer is already thinking about the project.
              </p>
              <p className='font-medium text-white'>
                The quote should be the next step — not another round of
                questions.
              </p>
            </div>

            <ul className='space-y-3'>
              {BENEFITS.map((item) => (
                <li key={item} className='flex gap-3 text-white'>
                  <span className='mt-1 flex-none text-jade-soft' aria-hidden='true'>
                    ✓
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function KeepYourProcess() {
  return (
    <section className='px-6 py-16 md:py-24'>
      <div className='container mx-auto max-w-3xl'>
        <motion.div {...RISE}>
          <Eyebrow>Start without changing how you sell</Eyebrow>
          <Heading>Fix the intake. Keep your quoting process.</Heading>
          <div className='mt-6 space-y-4 text-lg leading-relaxed text-muted'>
            <p>
              The Quote Builder gives customers a guided way to specify
              what they need.
            </p>
            <p>
              You receive the details, review the request, and prepare the quote
              the way you already do. There are no public prices and no online
              checkout.
            </p>
            <p>
              Just better information coming in — and less time spent tracking
              it down.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export function WhatsIncluded() {
  return <section className='tc-section tc-section--border'>
    <div className='tc-container tc-container--reading'>
      <Eyebrow>Build your quoting workflow</Eyebrow>
      <Heading>Start with the information your shop needs.</Heading>
      <p className='tc-lead mt-5'>Define the product choices, dimensions, materials, and quantities customers should provide. Review how requests reach your team, then discuss the tools and setup help you need.</p>
      <p className='mt-4 text-base text-text-light/80'>Your written proposal will specify capabilities, users, setup responsibilities, and costs before you commit.</p>
    </div>
  </section>;
}

export function QuoteBuilderCta() {
  return (
    <section className='px-6 py-16 md:py-24'>
      <div className='container mx-auto max-w-3xl text-center'>
        <motion.div {...RISE}>
          <Eyebrow>Start collecting better quote requests</Eyebrow>
          <Heading>Bring a product to your demo.</Heading>
          <p className='mt-6 text-lg leading-relaxed text-muted'>
            Set up your products, add the builder to your site, and give
            customers a clear way to tell you exactly what they need.
          </p>
          <div className='mt-8 flex justify-center'>
            <Link
              href='/demo'
              className='inline-flex items-center gap-2 rounded-full bg-jade-cta px-8 py-4 font-semibold text-obsidian transition-colors hover:bg-jade-cta-deep'
            >
              Book a demo
              <span aria-hidden='true'>→</span>
            </Link>
          </div>
          <p className='mt-5 text-sm text-muted'>
            Discuss your quote-request workflow with our team.
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export function UpgradePath() {
  return (
    <section className='border-t border-line/60 bg-onyx/40 px-6 py-16 md:py-20'>
      <div className='container mx-auto max-w-3xl'>
        <motion.div {...RISE}>
          <h2 className='font-display text-2xl font-bold text-white sm:text-3xl'>
            Ready to go beyond quote requests?
          </h2>
          <p className='mt-4 text-lg leading-relaxed text-muted'>
            Turn custom configurations into online orders. TimberCloud helps
            custom manufacturers sell, price, build, and ship from one system.
          </p>
          <p className='mt-4 leading-relaxed text-muted'>
            Discuss quote requests, online selling, and the connected tools your shop needs. We’ll document the agreed scope in your proposal.
          </p>
          <div className='mt-7 flex flex-wrap gap-4'>
            <Link
              href='/pricing'
              className='inline-flex items-center gap-2 rounded-full border border-jade-deep/60 px-6 py-3 font-semibold text-jade-soft transition-colors hover:border-jade-soft hover:text-white'
            >
              Discuss tools and pricing
            </Link>
            <DemoCtaLink
              href='/demo'
              ctaLocation='quote_builder'
              className='inline-flex items-center gap-2 rounded-full border border-line px-6 py-3 font-semibold text-white transition-colors hover:border-jade-soft hover:text-jade-soft'
            >
              Book a demo
            </DemoCtaLink>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
